import { motion } from 'motion/react';
import dgStars from '../../assets/dg-stars.gif';
import { useLazyLoadQuery } from 'react-relay';
import { getInferredDiagnosticSkills } from '../../graphQLOperations/Diagnostic';
import { DiagnosticInferredSkillsQuery } from '../../graphQLOperations/__generated__/DiagnosticInferredSkillsQuery.graphql';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { Dispatch, FC, SetStateAction } from 'react';
import { Button } from 'primereact/button';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  setShowInferredSkills: Dispatch<SetStateAction<boolean>>;
}

export const DiagnosticStars: FC<Props> = ({ setShowInferredSkills }) => {
  const user = useAppSelector(selectUser);

  const data = useLazyLoadQuery<DiagnosticInferredSkillsQuery>(getInferredDiagnosticSkills, {
    studentId: `StudentNode:${user?.id}`,
    userId: `UserNode:${user?.id}`,
  });

  const demonstratedSkills = data.studentsDemonstratedSkillsReport[0]?.demonstratedSkills ?? 0;

  const roadmapSkillsTotal = new Set(
    (data.currentTargetGroupByStudentId?.targetGroup?.targets ?? [])
      .flatMap(target => target?.targetSkills ?? [])
      .filter(
        targetSkill => targetSkill?.isActive && targetSkill.targetSkillType?.name !== TargetSkillTypeLabel.EXTENSION,
      )
      .map(targetSkill => targetSkill?.skill?.id)
      .filter((skillId): skillId is string => skillId != null),
  ).size;

  const getTextVariant = () => {
    const variants = {
      low: {
        header: 'Your roadmap is ready!',
        title: (
          <>
            Great effort{' '}
            <MaskedStudentData student={{ publicUserId: user?.sub }} type={PiiTypes.FirstName}>
              {user?.firstName}
            </MaskedStudentData>
            !
          </>
        ),
        sub: `There's plenty of exciting stuff ahead.`,
      },
      mid: {
        header: (
          <>
            Well done,{' '}
            <MaskedStudentData student={{ publicUserId: user?.sub }} type={PiiTypes.FirstName}>
              {user?.firstName}
            </MaskedStudentData>
            !
          </>
        ),
        title: 'Your roadmap is ready.',
        sub: 'Let your journey begin!',
      },
      high: {
        header: (
          <>
            Wow{' '}
            <MaskedStudentData student={{ publicUserId: user?.sub }} type={PiiTypes.FirstName}>
              {user?.firstName}
            </MaskedStudentData>
            , you crushed it!
          </>
        ),
        title: 'Amazing job!',
        sub: `You're starting ahead!`,
      },
    };

    const percentage = roadmapSkillsTotal > 0 ? (demonstratedSkills / roadmapSkillsTotal) * 100 : 0;
    if (percentage <= 24) {
      return variants.low;
    } else if (percentage <= 50) {
      return variants.mid;
    } else {
      return variants.high;
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-justify-center paper-grid-bg !tw-bg-[#F9F8F5]">
      <div className="tw-mt-[15vh]">
        <img alt="stars" src={dgStars} className="tw-h-[30rem] tw-w-[45rem]" />
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            delay: 3,
            ease: [0.34, 1.56, 0.64, 1],
          }}
          style={{ position: 'relative', top: -300 }}
        >
          <h1 className="tw-text-center tw-text-[2rem] tw-mb-3">{getTextVariant().header}</h1>
          <h1 className="tw-text-center">
            <span style={{ fontWeight: 400 }}>You </span>
            <b>demonstrated {demonstratedSkills} skills</b>
          </h1>
          <h1 className="tw-text-center tw-my-2">{getTextVariant().title}</h1>
          <p className="tw-text-center">{getTextVariant().sub}</p>

          <div className="tw-flex tw-justify-center">
            <Button label="Start my journey!" onClick={() => setShowInferredSkills(false)} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
